import { useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useExternalPointStore } from '@stores/external-point-store';
import { externalPointService } from '@features/external-point/service';

/**
 * 외부 포인트(협력기관) 감지 훅.
 * - URL의 ?ref= 파라미터만 사용 (통합 뷰는 항상 전체 노출)
 * - ?ref= 진입 시 누구나 해당 스토어 진입 가능 (다운로드 시점에서 회원 검증)
 */
export const useExternalPointDetection = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { activePoint, setActivePoint, clearActivePoint, beginResolving, clearResolving } = useExternalPointStore();
  const resolvedCode = useRef<string | null>(null);

  const isProductsPage = location.pathname === '/products' || location.pathname.startsWith('/products/');
  const refCode = searchParams.get('ref');

  useEffect(() => {
    if (!isProductsPage) return;

    const reset = () => {
      clearActivePoint();
      resolvedCode.current = null;
    };

    if (!refCode) {
      reset();
      return;
    }

    // 이미 resolve된 코드면 스킵
    if (resolvedCode.current === refCode && activePoint?.code === refCode) {
      return;
    }

    if (activePoint?.code === refCode) {
      resolvedCode.current = refCode;
      clearResolving();
      return;
    }

    beginResolving(refCode);

    const resolve = async () => {
      try {
        const res = await externalPointService.getByCode(refCode);
        if (res.success && res.data && res.data.isActive) {
          setActivePoint({
            code: res.data.code,
            name: res.data.name,
            headerImageUrl: res.data.headerImageUrl,
          });
          resolvedCode.current = refCode;
        } else {
          reset();
        }
      } catch {
        reset();
      }
    };

    void resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductsPage, refCode]);
};
